import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, X, ExternalLink, AlertCircle } from "lucide-react";
import { 
  selectAssessmentData,
  selectAssessmentLoading,
  selectAssessmentError,
  selectDependenciesChanged
} from "../redux/assessmentSlice";
import Sidebar from "../components/Sidebar";
import Loader from "../components/loader/Loader";

const TaskAutomationAnalysis = () => {
  const navigate = useNavigate();
  const { currentJob, occupationId, currentJobDescription, hasAIJobImpact } = useSelector((state) => state.user);
  
  // Get data from Redux
  const assessmentData = useSelector(selectAssessmentData);
  const isLoading = useSelector(selectAssessmentLoading);
  const error = useSelector(selectAssessmentError);
  const dependenciesChanged = useSelector(selectDependenciesChanged);

  const [modal, setModal] = useState({ type: null, data: null });

  const openModal = (type, data) => setModal({ type, data });
  const closeModal = () => setModal({ type: null, data: null });

  // Check if user has completed AI Job Impact Assessment
  if (!hasAIJobImpact) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Assessment Required</h2>
            <p className="text-gray-600 mb-6">
              You need to complete the AI Job Impact Assessment before viewing task automation analysis.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/dashboard/career-assessment/ai-job-impact-assessment")}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Complete AI Job Impact Assessment
              </button>
              <button
                onClick={() => navigate(-1)}
                className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  // Check if required data is missing
  if (!occupationId || !currentJob || !currentJobDescription) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Missing Job Information</h2>
            <p className="text-gray-600 mb-6">
              Please complete your profile with job information before viewing task automation analysis.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/dashboard/profile")}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Complete Profile
              </button>
              <button
                onClick={() => navigate(-1)}
                className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  const transformTasks = (tasks, isHighRisk = true) =>
    tasks
      ?.map(({ task_name, risk_score, task_description }) => ({
        name: task_name,
        percentage: parseFloat(risk_score),
        description: task_description,
      }))
      .sort((a, b) => isHighRisk ? b.percentage - a.percentage : a.percentage - b.percentage) || [];

  const transformTools = (tools = []) =>
    tools.reduce(
      (acc, { primary_function, tool_name, description, specific_tasks_affected, official_website_link }) => {
        const formatted = { 
          name: tool_name, 
          description, 
          specificTasks: specific_tasks_affected,
          officialWebsiteLink: official_website_link
        };
        
        const category = primary_function?.toLowerCase();
        
        // Only add to augment or automate categories, default to augment for unknown categories
        if (category === 'automate') {
          acc.automate.push(formatted);
        } else {
          acc.augment.push(formatted);
        }
        
        return acc;
      },
      { augment: [], automate: [] }
    );

  const transformSkills = (skills = []) =>
    skills.map(({ skill_name, relevance_in_ai_era }) => ({
      name: skill_name,
      relevance: relevance_in_ai_era,
    }));

  if (isLoading) return <Loader />;

  if (error)
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Unable to Load Assessment</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Go Back
            </button>
          </div>
        </div>
      </Sidebar>
    );

  // Check if assessment data is available
  if (!assessmentData && !isLoading) {
    return (
      <Sidebar>
        <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">No Assessment Data</h2>
            <p className="text-gray-600 mb-6">
              {error || "Assessment data is not available. Please complete the AI Job Impact Assessment first."}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/dashboard/career-assessment/ai-job-impact-assessment")}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Complete AI Job Impact Assessment
              </button>
              <button
                onClick={() => navigate(-1)}
                className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </Sidebar>
    );
  }

  const {
    ai_job_impact_score = 45,
    tasks_high_automation_risk = [],
    tasks_low_automation_risk = [],
    ai_tools = [],
    future_proof_skills = [],
  } = assessmentData || {};

  const highAutomationTasks = transformTasks(tasks_high_automation_risk);
  const lowAutomationTasks = transformTasks(tasks_low_automation_risk, false);
  const { augment: augmentTools, automate: automateTools } = transformTools(ai_tools);
  const futureProofSkills = transformSkills(future_proof_skills);

  const renderTasks = (tasks, color, type) => {
    return tasks.length ? (
      tasks.map((task, i) => (
        <div
          key={i}
          onClick={() => openModal("task", { ...task, type })}
          className="cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
        >
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-700">{task.name}</span>
            <span className={`text-sm font-semibold text-${color}-600`}>{task.percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`bg-${color}-500 h-2 rounded-full`}
              style={{ width: `${task.percentage}%` }}
            />
          </div>
        </div>
      ))
    ) : (
      <p className="text-gray-500 text-center py-4">No {type} automation risk tasks found.</p>
    );
  };

  const renderToolChips = (tools, onClick) =>
    tools.length ? (
      tools.map((tool, i) => (
        <span
          key={i}
          onClick={() => onClick(tool)}
          className="px-3 py-1 bg-gray-100 text-blue-700 text-xs rounded-full cursor-pointer hover:bg-gray-200 transition"
        >
          {tool.name}
        </span>
      ))
    ) : (
      <span className="text-gray-500 text-sm">No tools available</span>
    );

  return (
    <Sidebar>
      <div className="min-h-screen p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-blue-800 mb-2">Task Automation Analysis</h1>
            <p className="text-gray-600">Discover which tasks are most vulnerable to AI automation.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-1 bg-white border border-purple-200 rounded-lg hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mt-0.5" />
              Back
            </button>
            <button
              onClick={() => navigate("/dashboard/career-discovery/career-recommendations")}
              className="px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              View Recommended Careers
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-3 space-y-8">
            {/* High Risk */}
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <h2 className="text-xl font-bold text-blue-800 mb-6">Relatively High Automation Tasks</h2>
              <div className="space-y-4">{renderTasks(highAutomationTasks, "red", "high")}</div>
            </div>

            {/* Low Risk */}
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <h2 className="text-xl font-bold text-blue-800 mb-6">Relatively Low Automation Tasks</h2>
              <div className="space-y-4">{renderTasks(lowAutomationTasks, "green", "low")}</div>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tools */}
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-2">Tools to Automate/Augment Your Job</h3>
              <p className="text-sm text-gray-600 mb-6">Explore apps to replace or enhance tasks</p>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Automation</h4>
                  <div className="flex flex-wrap gap-2">{renderToolChips(automateTools, (t) => openModal("tool", t))}</div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Augmentation</h4>
                  <div className="flex flex-wrap gap-2">{renderToolChips(augmentTools, (t) => openModal("tool", t))}</div>
                </div>
              </div>
            </div>

            {/* Future-Proof Skills */}
            <div className="bg-white p-6 rounded-xl border shadow-sm">
              <h3 className="text-lg font-bold text-blue-800 mb-2">Future-Proof Skills</h3>
              <p className="text-sm text-blue-600 mb-6">Skills that remain valuable in the AI era</p>
              {futureProofSkills.length ? (
                <div className="rounded-lg overflow-hidden">
                  <div className="border-b p-2 mb-4 grid grid-cols-2 text-sm font-medium text-gray-700">
                    <span>Skill Name</span>
                    <span>Relevance in the AI Era</span>
                  </div>
                  <div className="divide-y text-xs text-gray-600">
                    {futureProofSkills.map((skill, i) => (
                      <div key={i} className="grid grid-cols-2 gap-4 p-2">
                        <span className="font-semibold">{skill.name}</span>
                        <span>{skill.relevance}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No future-proof skills available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Task/Tool Modal */}
      {modal.data && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999]" onClick={closeModal}></div>
          <div className="fixed inset-0 flex items-center justify-center z-[10000] p-4 pointer-events-none">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto pointer-events-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-bold text-gray-800">{modal.data.name}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 space-y-4 text-sm text-gray-600">
                {modal.type === "task" ? (
                  <>
                    <p>{modal.data.description || "No description available."}</p>
                    <div className="bg-gray-50 p-4 border rounded-lg flex justify-between">
                      <span className="font-medium text-gray-700">Automation score:</span>
                      <span className="font-bold text-gray-900">{modal.data.percentage}%</span>
                    </div>
                  </>
                ) : (
                  <>
                    <p>{modal.data.description}</p>
                    <div>
                      <h4 className="text-sm font-semibold text-blue-700 mb-2">Specific Tasks Affected:</h4>
                      <ul className="space-y-1">
                        {modal.data.specificTasks.map((t, i) => (
                          <li key={i} className="flex gap-2 items-start">
                            <span className="w-1 h-1 bg-blue-500 rounded-full mt-2"></span>
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {modal.data.officialWebsiteLink && (
                      <div className="pt-4 border-t">
                        <a
                          href={modal.data.officialWebsiteLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                        >
                          Visit {modal.data.name}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </Sidebar>
  );
};

export default TaskAutomationAnalysis;
